// Contact Form Component
// Handles work opportunity and consulting inquiries
// Uses React state to manage form inputs

import React, { useState, useRef, useEffect } from 'react';
import './ContactForm.css';
import {
  checkRateLimit,
  recordSubmission,
  sanitizeFormData,
  validateContact
} from '../utils/formProtection';
import { SERVICE_NAMES } from '../constants';

function ContactForm() {
  // useState hooks to manage form input values
  // Each input field has its own state variable
  const [formData, setFormData] = useState({
    name: '',
    contactType: 'Email',
    contact: '',
    company: '',
    subject: '',
    message: '',
    website: '' // Honeypot field - should remain empty
  });

  // State to track if form was submitted
  const [submitted, setSubmitted] = useState(false);

  // Function to handle input changes
  // This is called whenever user types in any input field
  const handleChange = (e) => {
    // e.target.name is the name attribute of the input
    // e.target.value is what the user typed
    // We update the state with the new value
    setFormData({
      ...formData, // Spread operator - keeps existing values
      [e.target.name]: e.target.value // Updates only the changed field
    });
  };

  // State to track loading and errors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ref to store timeout ID for cleanup
  const resetTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  // Read service + message from hash (#contact?service=...&message=...) and sync on hash changes
  useEffect(() => {
    const applyHashParams = () => {
      const hash = window.location.hash || '';
      if (!hash.startsWith('#contact')) return;

      const query = hash.includes('?') ? hash.split('?')[1] : '';
      const params = new URLSearchParams(query);
      const serviceId = params.get('service');
      const messageRaw = params.get('message');

      setFormData((prev) => {
        const next = { ...prev };
        if (serviceId && SERVICE_NAMES[serviceId]) {
          next.subject = SERVICE_NAMES[serviceId];
        }
        if (messageRaw !== null && messageRaw !== '') {
          try {
            next.message = decodeURIComponent(messageRaw);
          } catch {
            next.message = messageRaw;
          }
        }
        return next;
      });
    };

    applyHashParams();
    window.addEventListener('hashchange', applyHashParams);
    return () => window.removeEventListener('hashchange', applyHashParams);
  }, []);

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevents page refresh (default form behavior)
    setLoading(true);
    setError('');
    
    try {
      // Honeypot check - if website field is filled, it's likely a bot
      if (formData.website && formData.website.trim() !== '') {
        // Silently fail - don't alert bots
        setLoading(false);
        return;
      }
      
      // Rate limiting check
      const rateLimitCheck = checkRateLimit();
      if (!rateLimitCheck.allowed) {
        setError(rateLimitCheck.message);
        setLoading(false);
        return;
      }
      
      // Validate contact information
      const contactValidation = validateContact(formData.contact, formData.contactType);
      if (!contactValidation.valid) {
        setError(contactValidation.message);
        setLoading(false);
        return;
      }
      
      // Sanitize all inputs
      const sanitizedData = sanitizeFormData(formData);
      
      // Validate required fields after sanitization
      if (!sanitizedData.name || sanitizedData.name.trim() === '') {
        setError('Name is required');
        setLoading(false);
        return;
      }
      
      if (!sanitizedData.message || sanitizedData.message.trim() === '') {
        setError('Message is required');
        setLoading(false);
        return;
      }
      
      if (!sanitizedData.subject || sanitizedData.subject.trim() === '') {
        setError('Subject is required');
        setLoading(false);
        return;
      }
      
      // Submit to Formspree
      const response = await fetch(process.env.REACT_APP_FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: sanitizedData.name,
          contactType: sanitizedData.contactType,
          contact: sanitizedData.contact,
          company: sanitizedData.company,
          subject: sanitizedData.subject,
          message: sanitizedData.message,
          _subject: `Work Opportunity: ${sanitizedData.subject}` // Email subject line
        })
      });

      if (response.ok) {
        // Record submission for rate limiting
        recordSubmission();
        
        // Success - form submitted
        setSubmitted(true);
        
        // Clear any existing timeout before setting a new one
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
        }
        
        // Reset form after 3 seconds
        resetTimeoutRef.current = setTimeout(() => {
          setFormData({
            name: '',
            contactType: 'Email',
            contact: '',
            company: '',
            subject: '',
            message: '',
            website: ''
          });
          setSubmitted(false);
          resetTimeoutRef.current = null;
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-form-wrapper">
      <h3 className="contact-form-title">Work Opportunities & Consulting</h3>

      {submitted ? (
        // Success message shown after form submission
        <div className="form-success">
          <div className="success-icon">✓</div>
          <p>Thank you! Your message has been sent. I'll get back to you soon.</p>
        </div>
      ) : (
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="contact-form-body">
          {/* Name input */}
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              required // HTML5 validation - field must be filled
              placeholder="Your name"
              maxLength={100}
            />
          </div>

          {/* Contact input */}
          <div className="form-group">
            <label htmlFor="contact" className="form-label">
              Contact *
            </label>
            <div className="contact-input-wrapper">
              <select
                id="contactType"
                name="contactType"
                className="form-input"
                value={formData.contactType}
                onChange={handleChange}
                style={{ flex: '0 0 140px' }}
              >
                <option value="Email">Email</option>
                <option value="Telegram">Telegram</option>
                <option value="X">X</option>
              </select>
              <input
                type={formData.contactType === 'Email' ? 'email' : 'text'}
                id="contact"
                name="contact"
                className="form-input"
                value={formData.contact}
                onChange={handleChange}
                required
                placeholder={
                  formData.contactType === 'Email' 
                    ? 'your.email@example.com'
                    : formData.contactType === 'Telegram'
                    ? '@your_telegram_username'
                    : '@your_x_username'
                }
                style={{ flex: '1' }}
                maxLength={200}
              />
            </div>
          </div>

          {/* Company input */}
          <div className="form-group">
            <label htmlFor="company" className="form-label">
              Company / Project
            </label>
            <input
              type="text"
              id="company"
              name="company"
              className="form-input"
              value={formData.company}
              onChange={handleChange}
              placeholder="Your company or project name"
              maxLength={200}
            />
          </div>

          {/* Subject input */}
          <div className="form-group">
            <label htmlFor="subject" className="form-label">
              Subject *
            </label>
            <select
              id="subject"
              name="subject"
              className="form-input"
              value={formData.subject}
              onChange={handleChange}
              required
            >
              <option value="">Select a topic...</option>
              <option value="AI Agent Setup & Optimization">AI Agent Setup & Optimization</option>
              <option value="Business Development">Business Development</option>
              <option value="Website & Web App">Website & Web App</option>
              <option value="Web3 Project Consulting">Web3 Project Consulting</option>
              <option value="Partnership Opportunity">Partnership Opportunity</option>
              <option value="Marketing inquiries">Marketing inquiries</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Message textarea */}
          <div className="form-group">
            <label htmlFor="message" className="form-label">
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              className="form-input form-textarea"
              value={formData.message}
              onChange={handleChange}
              required
              rows="3"
              placeholder="Tell me about your project or opportunity..."
              maxLength={5000}
            ></textarea>
          </div>

          {/* Honeypot field - hidden from users, bots will fill it */}
          <div className="form-honeypot">
            <label htmlFor="website">Website (leave blank)</label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="form-error">
              {error}
            </div>
          )}
          </div>

          <button type="submit" className="form-submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      )}
    </div>
  );
}

export default ContactForm;

